// Import A-Frame directly
import 'aframe';
import { useEffect, useRef } from 'react';
import './aframe-components';

// Fix THREE import
declare const THREE: any;

export default function ModelViewer() {
  const sceneRef = useRef<HTMLElement | null>(null);
  const visibilityChangeRef = useRef<boolean>(false);
  const scriptsLoadedRef = useRef<boolean>(false);
  
  useEffect(() => {
    // Variables for cleanup
    let oceanScript: HTMLScriptElement | null = null;
    let extrasScript: HTMLScriptElement | null = null;
    
    console.log('ModelViewer mounted - initializing A-Frame scene');
    
    // Load required scripts
    const loadScripts = () => {
      if (!scriptsLoadedRef.current) {
        // Load ocean script
        oceanScript = document.createElement('script');
        oceanScript.src = '/combo-ocean.js';
        oceanScript.onload = () => console.log('Ocean script loaded');
        document.head.appendChild(oceanScript);
        
        // Load A-Frame extras
        extrasScript = document.createElement('script');
        extrasScript.src = 'https://cdn.jsdelivr.net/gh/c-frame/aframe-extras@7.1.0/dist/aframe-extras.min.js';
        document.head.appendChild(extrasScript);
        
        scriptsLoadedRef.current = true;
      }
    };
    
    loadScripts();
    
    // Handle visibility change
    const handleVisibilityChange = () => {
      if (!document.hidden && visibilityChangeRef.current) {
        const oceanEl = document.querySelector('#ocean-surface');
        if (oceanEl && (oceanEl as any).components['wobble-geometry']) {
          const wg = (oceanEl as any).components['wobble-geometry'];
          if (typeof wg.play === 'function') {
            wg.play();
          }
        }
      }
      visibilityChangeRef.current = document.hidden;
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  return (
    <div className="w-full h-full rounded-2xl overflow-hidden">
      <a-scene 
        embedded 
        ref={sceneRef} 
        tabIndex={0}
        vr-mode-ui="enabled: false"
        renderer="colorManagement: true; physicallyCorrectLights: true;" 
        ensure-waves
        camera-rotation-initializer="position: -16.9 10.9 -20.4; rotation: -7 199 0"
        controls-helper="minHeight: 9.0; maxHeight: 20.0; boundsX: -50, 50; boundsZ: -100, 30"
        debug-display="enabled: true">
        <a-assets>
          <a-asset-item id="glb-model" src="/untitled.glb"></a-asset-item>
          <audio id="ocean-ambient" src="/ocean-sea.wav" preload="auto" loop></audio>
          <img id="water-normal" src="/waternormals.jpg" crossOrigin="anonymous" />
        </a-assets>

        <a-ocean-plane 
          id="ocean-surface"
          position="0 8 -90"
          width="500" 
          height="400"
          rotation="-90 0 0"
          color="#00d4d4"
          opacity="0.6"
          metalness="0.6" 
          roughness="0.4"
          material="normalMap: #water-normal; normalTextureRepeat: 50 50; emissive: #00a3a3; emissiveIntensity: 0.3"
          wobble-normal
          wobble-geometry="amplitude: 0.2; speed: 0.5"
        ></a-ocean-plane>
        
        <a-gltf-model
          src="#glb-model"
          position="0 5.5 -10"
          rotation="0 0 0"
          scale="1 1 1"
        ></a-gltf-model>
        
        <a-sky src="/miami360.jpg" rotation="0 60 0"></a-sky>
        
        <a-entity light="type: ambient; color: #BBB; intensity: 1.0"></a-entity>
        <a-entity light="type: directional; color: #FFF; intensity: 1.8; castShadow: true" position="0 15 5"></a-entity>
        <a-entity light="type: directional; color: #ffd35f; intensity: 0.6" position="-10 12 -10"></a-entity>
        <a-entity light="type: directional; color: #ffd35f; intensity: 0.6" position="10 12 10"></a-entity>
        
        <a-entity
          id="custom-camera"
          camera
          look-controls="reverseMouseDrag: false; touchEnabled: true; mouseEnabled: true"
          position="-16.9 10.9 -20.4"
          rotation="-7 199 0"
        ></a-entity>
        
        <a-text 
          value="WASD/Arrows: Move | Q/E: Up/Down | Mouse: Look" 
          color="#ffffff" 
          position="-1.5 6 -4" 
          scale="0.8 0.8 0.8"
        ></a-text>
        
        <a-text 
          value="Click in the scene to activate keyboard controls" 
          color="#ffffff" 
          position="-2.2 7 -4" 
          scale="0.7 0.7 0.7"
        ></a-text>
        
        <a-text 
          value="Click and drag with mouse to look around" 
          color="#ffff00" 
          position="-2.5 8 -4" 
          scale="0.7 0.7 0.7"
        ></a-text>
      </a-scene>
    </div>
  );
} 